/**
 * Navigation Store for AccountsGenie
 * Manages navigation state, breadcrumbs, and sidebar state
 * Following PRD Phase 2 navigation specifications
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export interface NavigationState {
  // Current page state
  currentPage: string;
  breadcrumbs: BreadcrumbItem[];
  
  // Sidebar state
  sidebarCollapsed: boolean;
  mobileMenuOpen: boolean;
  
  // Navigation history
  navigationHistory: string[];
  
  // Actions
  setCurrentPage: (page: string) => void;
  setBreadcrumbs: (crumbs: BreadcrumbItem[]) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  addToHistory: (path: string) => void;
  clearHistory: () => void;
  
  // Helper functions
  generateBreadcrumbs: (pathname: string) => BreadcrumbItem[];
  getPageTitle: (pathname: string) => string;
}

// Page configuration for breadcrumbs and titles
const pageConfig: Record<string, { title: string; label: string; parent?: string }> = {
  '/': { title: 'Dashboard Overview', label: 'Dashboard' },
  '/dashboard': { title: 'Dashboard Overview', label: 'Dashboard' },
  '/chat': { title: 'AI Assistant Chat', label: 'AI Chat' },
  '/audit': { title: 'Audit Log & History', label: 'Audit Log' },
  '/reports': { title: 'Financial Reports', label: 'Reports' },
  '/settings': { title: 'Account Settings', label: 'Settings' },
  '/help': { title: 'Help & Support', label: 'Help' }
};

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentPage: '/dashboard',
      breadcrumbs: [{ label: 'Dashboard' }],
      sidebarCollapsed: false,
      mobileMenuOpen: false,
      navigationHistory: [],

      // Actions
      setCurrentPage: (page: string) => {
        const { generateBreadcrumbs, addToHistory } = get();
        
        set({
          currentPage: page,
          breadcrumbs: generateBreadcrumbs(page),
          mobileMenuOpen: false // Close mobile menu when navigating
        });
        
        addToHistory(page);
      },

      setBreadcrumbs: (crumbs: BreadcrumbItem[]) => {
        set({ breadcrumbs: crumbs });
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
      },

      toggleMobileMenu: () => {
        set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen }));
      },

      closeMobileMenu: () => {
        set({ mobileMenuOpen: false });
      },

      addToHistory: (path: string) => {
        set((state) => ({
          navigationHistory: [
            path,
            ...state.navigationHistory.filter(p => p !== path)
          ].slice(0, 10) // Keep only last 10 entries
        }));
      },

      clearHistory: () => {
        set({ navigationHistory: [] });
      },

      // Helper functions
      generateBreadcrumbs: (pathname: string): BreadcrumbItem[] => {
        const breadcrumbs: BreadcrumbItem[] = [
          { label: 'Home', path: '/dashboard' }
        ];

        const config = pageConfig[pathname];
        if (config && pathname !== '/' && pathname !== '/dashboard') {
          breadcrumbs.push({ label: config.label });
        }

        return breadcrumbs;
      },

      getPageTitle: (pathname: string): string => {
        const config = pageConfig[pathname];
        return config?.title || 'AccountsGenie';
      }
    }),
    {
      name: 'navigation-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentPage: state.currentPage,
        sidebarCollapsed: state.sidebarCollapsed,
        navigationHistory: state.navigationHistory,
      }),
    }
  )
);